import { useNavigate } from 'react-router-dom';
import {
  Users,
  Target,
  Waypoints,
  Ban,
  FileText,
  Scale,
  Newspaper,
  Flag,
  ShieldAlert,
  AlertTriangle,
  Gauge,
} from 'lucide-react';
import {
  Badge,
  EmptyState,
  PageHeader,
  Spinner,
  type BadgeTone,
} from '../../ui';
import { HomePanel, LinkRow, StatTile } from '../common/HomePanels';
import { useTeamVector } from '../../features/home/useHome';
import type { GoalStatusCounts, MilestoneItem } from '../../features/home/homeModel';

/** "due in / overdue" caption for a milestone. */
function milestoneWhen(m: MilestoneItem): string {
  if (m.overdue) {
    const d = Math.abs(m.daysUntil);
    return `Overdue ${d}d`;
  }
  if (m.daysUntil === 0) return 'Due today';
  return `In ${m.daysUntil}d`;
}

/** Renders the BHAG (or all-goal) on-track / at-risk / off-track breakdown. */
function GoalHealthBreakdown({ counts }: { counts: GoalStatusCounts }) {
  const chips: { label: string; value: number; tone: BadgeTone }[] = [
    { label: 'On track', value: counts.onTrack, tone: 'success' },
    { label: 'At risk', value: counts.atRisk, tone: 'warning' },
    { label: 'Off track', value: counts.offTrack, tone: 'danger' },
    { label: 'Completed', value: counts.completed, tone: 'info' },
    { label: 'Not started', value: counts.notStarted, tone: 'neutral' },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {chips
        .filter((c) => c.value > 0)
        .map((c) => (
          <Badge key={c.label} tone={c.tone} dot>
            {c.value} {c.label}
          </Badge>
        ))}
      {counts.total === 0 && <span className="text-sm text-zinc-500">No goals tagged yet.</span>}
    </div>
  );
}

/**
 * Team Vector — the team home (section 26). A single shared engineering picture
 * everyone sees the same way: goal health (BHAG on-track/at-risk), sprint
 * delivery progress, active workstreams, blockers, new documents & decisions,
 * major changes, upcoming milestones and delivery risks. Managers and engineers
 * get identical context. Awareness only — no productivity/velocity/ranking.
 */
export function TeamVectorContent() {
  const navigate = useNavigate();
  const { model, milestones, loading, error } = useTeamVector();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading Team Vector" />
      </div>
    );
  }
  if (error || !model) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Couldn't load the team view"
        description={error ?? 'No context available yet.'}
      />
    );
  }

  // Prefer the BHAG breakdown; fall back to all-goals when no BHAGs are tagged.
  const goalCounts = model.bhag.total > 0 ? model.bhag : model.allGoals;
  const goalScopeLabel = model.bhag.total > 0 ? 'BHAG goals' : 'All goals';

  return (
    <div>

      {/* Top stats */}
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Sprint progress"
          value={`${model.sprint.pct}%`}
          hint={`${model.sprint.done}/${model.sprint.total} stories done`}
          tone="accent"
        />
        <StatTile
          label="Active workstreams"
          value={model.activeWorkstreams.length}
          hint="in flight"
        />
        <StatTile
          label="Open blockers"
          value={model.openBlockers.length}
          hint={`${model.externalBlockerCount} external`}
          tone={model.openBlockers.length > 0 ? 'warning' : 'default'}
        />
        <StatTile
          label={goalScopeLabel}
          value={goalCounts.total}
          hint={`${goalCounts.onTrack} on track · ${goalCounts.atRisk + goalCounts.offTrack} need attention`}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Goal health */}
        <HomePanel
          title={`Goal health — ${goalScopeLabel}`}
          icon={Target}
          headerRight={
            <button
              type="button"
              onClick={() => navigate('/goals')}
              className="text-xs text-accent-fg hover:underline"
            >
              View goals
            </button>
          }
        >
          <div className="py-1">
            <GoalHealthBreakdown counts={goalCounts} />
          </div>
        </HomePanel>

        {/* Delivery risks */}
        <HomePanel
          title="Delivery risks"
          icon={ShieldAlert}
          count={model.deliveryRisks.length}
          empty={model.deliveryRisks.length === 0}
          emptyLabel="No delivery risks flagged from blockers or goal status."
        >
          {model.deliveryRisks.map((risk, i) => (
            <div
              key={i}
              className="flex items-start gap-2 px-2 py-1.5 text-sm text-zinc-300"
            >
              <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
              <span>{risk}</span>
            </div>
          ))}
        </HomePanel>

        {/* Active workstreams */}
        <HomePanel
          title="Active workstreams"
          icon={Waypoints}
          count={model.activeWorkstreams.length}
          empty={model.activeWorkstreams.length === 0}
          emptyLabel="No workstreams in flight."
        >
          {model.activeWorkstreams.slice(0, 8).map((w) => (
            <LinkRow
              key={w.id}
              primary={w.title}
              secondary={w.engineer}
              onClick={() => navigate(`/workstreams/${encodeURIComponent(w.id)}`)}
              trailing={<Badge tone="info">{w.currentStage}</Badge>}
            />
          ))}
        </HomePanel>

        {/* Blockers */}
        <HomePanel
          title="Open blockers"
          icon={Ban}
          count={model.openBlockers.length}
          empty={model.openBlockers.length === 0}
          emptyLabel="Nothing is blocking the team."
        >
          {model.openBlockers.slice(0, 8).map((b) => (
            <LinkRow
              key={b.id}
              primary={b.summary}
              secondary={`Blocked: ${b.blockedEngineer} · Owner: ${b.dependencyOwner}`}
              onClick={() => navigate('/blockers')}
              trailing={
                <Badge tone={b.internalExternal === 'EXTERNAL' ? 'warning' : 'neutral'}>
                  {b.internalExternal === 'EXTERNAL' ? 'External' : 'Internal'}
                </Badge>
              }
            />
          ))}
        </HomePanel>

        {/* New documents */}
        <HomePanel
          title="New documents this week"
          icon={FileText}
          count={model.newDocuments.length}
          empty={model.newDocuments.length === 0}
          emptyLabel="No new documents in the last week."
        >
          {model.newDocuments.slice(0, 8).map((d) => (
            <LinkRow
              key={d.id}
              primary={d.title}
              secondary={d.docType}
              onClick={() => navigate(`/documents/${encodeURIComponent(d.id)}`)}
              trailing={<span className="text-xs text-zinc-500">{d.ownerPoc}</span>}
            />
          ))}
        </HomePanel>

        {/* Decisions this week */}
        <HomePanel
          title="Decisions this week"
          icon={Scale}
          count={model.decisionsThisWeek.length}
          empty={model.decisionsThisWeek.length === 0}
          emptyLabel="No decisions logged in the last week."
        >
          {model.decisionsThisWeek.slice(0, 8).map((d) => (
            <LinkRow
              key={d.id}
              primary={d.decision}
              secondary={`Owner: ${d.owner}`}
              onClick={() => navigate(`/decisions/${encodeURIComponent(d.id)}`)}
            />
          ))}
        </HomePanel>

        {/* Major changes */}
        <HomePanel
          title="Major changes"
          icon={Newspaper}
          count={model.majorChanges.length}
          empty={model.majorChanges.length === 0}
          emptyLabel="No notable changes in the last week."
        >
          {model.majorChanges.map((c) => (
            <LinkRow
              key={c.id}
              primary={c.title}
              secondary={c.impact || c.why || c.changed}
              onClick={() => navigate('/changes')}
              trailing={<span className="text-xs text-zinc-500">{c.engineer}</span>}
            />
          ))}
        </HomePanel>

        {/* Upcoming milestones */}
        <HomePanel
          title="Upcoming milestones"
          icon={Flag}
          count={milestones.length}
          empty={milestones.length === 0}
          emptyLabel="No upcoming milestones across goals."
          className="lg:col-span-2"
        >
          {milestones.map((m) => (
            <LinkRow
              key={`${m.goalId}:${m.label}`}
              primary={m.label}
              secondary={m.goalTitle}
              onClick={() => navigate(`/goals/${encodeURIComponent(m.goalId)}`)}
              trailing={
                <Badge tone={m.overdue ? 'danger' : 'neutral'}>{milestoneWhen(m)}</Badge>
              }
            />
          ))}
        </HomePanel>
      </div>
    </div>
  );
}

/**
 * Team Vector — the team home (legacy standalone route). The unified Home page
 * now hosts this as its "Team" tab; this wrapper renders the standalone page
 * title + {@link TeamVectorContent}.
 */
export function TeamVectorPage() {
  return (
    <div>
      <PageHeader
        icon={<Users className="h-5 w-5" />}
        title="Team Vector"
        description="The team's shared engineering picture — goals, delivery, knowledge and risk. The same context for everyone."
      />
      <TeamVectorContent />
    </div>
  );
}

export default TeamVectorPage;
